import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { Select } from "@/components/ui/select";
import { UserRow } from "@/features/admin/users-table";
import { fetchAdminUsers, fetchAdminInvites, fetchAdminGroups, type AdminUser } from "@/lib/api";
import { PermissionGate } from "@/components/permission/permission-gate";
import { cn } from "@/lib/utils";
import type { AppUser, UserRole } from "@/types";

const STATUS_OPTIONS = ["Active", "Invited", "Suspended"] as const;

function toAppUser(u: AdminUser): AppUser {
  return {
    id: u.principalId,
    name: u.displayName || u.email,
    email: u.email,
    role: (u.roleName || u.principalType || "Organization Admin") as UserRole,
    groups: u.groupNames ? u.groupNames.split(",") : [],
    mfa: u.mfaMethod || "",
    status: u.status === "ACTIVE" ? "Active" : u.status === "DEACTIVATED" ? "Suspended" : u.status === "EXPIRED" ? "Suspended" : u.status === "INVITED" ? "Invited" : "Active",
    lastActive: u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—",
  };
}

function UserStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-[10px] border border-line bg-surface px-[18px] py-[16px]">
      <div className="text-[24px] font-bold leading-[1.1] tracking-tight text-ink">{value}</div>
      <div className="mt-0.5 text-[12px] font-medium text-muted">{label}</div>
    </div>
  );
}

export default function AdminUsersPage() {
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [group, setGroup] = useState("all");
  const [mfa, setMfa] = useState("all");
  const [status, setStatus] = useState("all");
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupOptions, setGroupOptions] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchAdminUsers(), fetchAdminInvites()])
      .then(([usersResult, invitesResult]) => {
        if (cancelled) return;
        if (usersResult.error && invitesResult.error) {
          setError(`Failed to load users: ${usersResult.error}`);
          setLoading(false);
          return;
        }
        const apiUsers: AppUser[] = !usersResult.error
          ? usersResult.users.map(toAppUser)
          : [];
        const invitedUsers: AppUser[] = !invitesResult.error && invitesResult.invites
          ? invitesResult.invites
              .filter(inv => inv.status === "PENDING" || inv.status === "ISSUED")
              .map(inv => ({
                id: inv.inviteId,
                name: inv.displayName || inv.email,
                email: inv.email,
                role: "Organization Admin" as UserRole,
                groups: [],
                mfa: "",
                status: "Invited" as const,
                lastActive: inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : "—",
              }))
          : [];
        setUsers([...apiUsers, ...invitedUsers]);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to connect to the admin API.");
        setLoading(false);
      });
    fetchAdminGroups().then((groupsResult) => {
      if (cancelled) return;
      if (!groupsResult.error && groupsResult.groups.length > 0) {
        setGroupOptions(groupsResult.groups.map((g) => g.name));
      }
    });
    return () => { cancelled = true; };
  }, []);

  const roleOptions = useMemo(
    () => [...new Set(users.map((u) => u.role))].sort(),
    [users],
  );

  const mfaOptions = useMemo(
    () => [...new Set(users.map((u) => u.mfa).filter(Boolean))].sort(),
    [users],
  );

  const stats = useMemo(
    () => ({
      total: users.length,
      active: users.filter((u) => u.status === "Active").length,
      pending: users.filter((u) => u.status === "Invited").length,
      suspended: users.filter((u) => u.status === "Suspended").length,
    }),
    [users]
  );

  const rows = useMemo(
    () =>
      users.filter((u) => {
        const q = query.toLowerCase();
        return (
          (!q || `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q)) &&
          (role === "all" || u.role === role) &&
          (group === "all" || u.groups.includes(group)) &&
          (mfa === "all" || u.mfa === mfa) &&
          (status === "all" || u.status === status)
        );
      }),
    [users, query, role, group, mfa, status]
  );

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <a href="#" onClick={(e) => e.preventDefault()} className="text-primary no-underline hover:underline">
          Identity
        </a>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Users</span>
      </nav>

      <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
        <UserStat value={String(stats.total)} label="Total Users" />
        <UserStat value={String(stats.active)} label="Active" />
        <UserStat value={String(stats.pending)} label="Pending" />
        <UserStat value={String(stats.suspended)} label="Suspended" />
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative w-[280px] max-w-full">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or role..."
            className="h-[36px] w-full rounded-[8px] border border-line bg-surface pl-9 pr-3 text-[13px] text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
          />
        </div>
        <Select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          aria-label="Filter by role"
          className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted"
        >
          <option value="all">All Roles</option>
          {roleOptions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </Select>
        <Select
          value={group}
          onChange={(e) => setGroup(e.target.value)}
          aria-label="Filter by group"
          className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted"
        >
          <option value="all">All Groups</option>
          {groupOptions.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </Select>
        <Select
          value={mfa}
          onChange={(e) => setMfa(e.target.value)}
          aria-label="Filter by MFA method"
          className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted"
        >
          <option value="all">All MFA</option>
          {mfaOptions.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </Select>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Filter by status"
          className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted"
        >
          <option value="all">All Status</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
        <PermissionGate permission="user:invite">
          <Link to="/admin/invite" className={cn(buttonVariants(), "ml-auto rounded-[8px]")}>
            + Invite User
          </Link>
        </PermissionGate>
      </div>

      {error && !loading && (
        <div className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {loading && (
        <div className="mb-4 text-center text-sm text-muted">Loading users...</div>
      )}

      <TableWrapper>
        <Table>
          <thead>
            <tr className="border-b border-line bg-bg">
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Name</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Email</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Role</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Groups</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">MFA</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Status</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Last Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <UserRow key={u.id} user={u} />
            ))}
          </tbody>
        </Table>
        {rows.length === 0 && !loading && !error && (
          <div className="px-4 py-10 text-center text-sm text-muted">No users match your filters.</div>
        )}
      </TableWrapper>
    </div>
  );
}
